"use client";

import CallExpandBtn from "@/components/v3/CallExpandBtn";
import ContentSearchInput from "@/components/v3/ContentSearchInput";
import CustomButton from "@/components/v3/CustomButton";
import { useWordPressContentSearch } from "@/hooks/useWordPressContentSearch";
import { ROUTES } from "@/lib/routes";
import { BlogPost } from "@/types";
import { Loader2, Play, RefreshCw } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import parse from "html-react-parser";

interface PodcastsProps {
  initialPosts: BlogPost[];
  totalPages: number;
}

const Podcasts = ({ initialPosts, totalPages }: PodcastsProps) => {
  const {
    posts,
    searchTerm,
    setSearchTerm,
    debouncedSearch,
    isSearching,
    isLoading,
    hasMore,
    loadMore,
    isSearchActive,
  } = useWordPressContentSearch({
    initialPosts,
    totalPages,
    apiPath: "/api/podcasts",
    perPage: 6,
  });

  const resultSummary =
    isSearchActive && !isSearching
      ? posts.length > 0
        ? `Showing results for "${debouncedSearch}"`
        : `No results for "${debouncedSearch}"`
      : undefined;

  return (
    <div className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-[100px]">
      <section className="container">
        <div className="flex flex-col gap-8 sm:gap-10 md:gap-12 lg:gap-[60px]">
          {/* Header */}
          <div className="flex flex-col items-start justify-between gap-6 sm:gap-8 md:flex-row md:items-center md:gap-10">
            <h2 className="font-crimsonText text-[28px] font-bold leading-tight text-[#02386D] sm:text-[32px] md:text-[42px] lg:text-[52px] xl:text-[62px]">
            Podcast Episodes for Personal
              <br />
              <span className="text-[#2857A7]">& Professional Growth</span>
            </h2>

            <div className="flex w-full shrink-0 flex-col gap-4 sm:gap-5 md:w-[526px]">
              <p className="font-onest text-[16px] font-normal leading-[110%] text-[#607090] sm:text-[18px] md:text-[20px]">
              Explore expert-led podcast episodes designed to build your emotional resilience, boost mental clarity, and provide practical strategies for positive change.
              </p>
              <div className="flex flex-col items-start justify-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                {/* <CallExpandBtn variant="dark" /> */}
                <CustomButton
                  label="Go to Youtube"
                  href="https://www.youtube.com/@Dose-Of-Growth"
                  variant="primary"
                  withIcon
                  size="lg"
                />
              </div>
            </div>
          </div>

          <ContentSearchInput
            id="podcast-search"
            placeholder="Search podcast episodes..."
            value={searchTerm}
            onChange={setSearchTerm}
            isSearching={isSearching}
            resultSummary={resultSummary}
          />

          {/* Cards */}
          {isSearching ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[#006DFF]"
                aria-hidden="true"
              />
              <p className="font-onest text-[16px] text-[#607090]">
                Searching podcasts...
              </p>
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center font-onest text-[16px] text-[#607090]">
              {isSearchActive
                ? `No podcasts found for "${debouncedSearch}".`
                : "No podcasts available at the moment."}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-[21px]">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  href={ROUTES.resources.counsellingPodcastPost(post.slug)}
                  className="group relative flex h-[280px] flex-col overflow-hidden rounded-[12px] border border-white/20 shadow-lg sm:h-[300px] sm:rounded-[14px] md:h-[320px] md:rounded-[16px]"
                >
                  {/* Background Image */}
                  <Image
                    src={post.featuredImage || "/images/v3/podcasts/podcast.png"}
                    alt={post.imageAlt}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                  />

                  {/* Gradient overlay */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[200px] sm:h-[220px] md:h-[250px]"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #006DFF 80%)",
                    }}
                  />

                  {/* Content */}
                  <div className="absolute inset-0 z-10 flex flex-col justify-end gap-2 p-4 text-white sm:gap-2.5 sm:p-5 md:gap-3 md:p-6">
                    {/* Date */}
                    <span className="font-onest text-[12px] font-medium text-white/80 sm:text-[13px] md:text-[14px]">
                      {post.date}
                    </span>

                    {/* Title */}
                    <h3 className="font-crimsonText text-[22px] font-bold leading-[1.1] text-white sm:text-[24px] md:text-[28px]">
                      {parse(post.title)}
                    </h3>

                    {/* Description */}
                    <p className="line-clamp-2 font-onest text-[12px] font-light leading-[140%] text-white/90 sm:text-[13px]">
                      {post.excerpt}
                    </p>

                    {/* Play badge */}
                    <div className="mt-0.5 sm:mt-1">
                      <div className="inline-flex items-center gap-1.5 rounded-[6px] border border-white/20 bg-white p-1 pr-3 shadow-sm sm:gap-2 sm:rounded-[8px] sm:p-1.5 sm:pr-4">
                        <div className="flex h-[20px] w-[20px] items-center justify-center rounded-md bg-[#006DFF] sm:h-[22px] sm:w-[22px]">
                          <Play
                            size={10}
                            className="translate-x-[1px] fill-white text-white"
                          />
                        </div>
                        <span className="font-onest text-[12px] font-semibold text-[#006DFF] sm:text-[13px]">
                          Listen Now
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="mx-auto mt-1 flex min-h-[44px] flex-row items-center justify-center gap-2 rounded-md border border-v3-primary px-5 py-2.5 text-sm text-v3-primary duration-300 hover:bg-v3-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:mt-2 sm:px-6 sm:py-3 sm:text-base"
            >
              {isLoading ? "Loading..." : "Load More"}{" "}
              <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Podcasts;
